import { useMemo } from "react";

import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CapabilityGate from "@/components/CapabilityGate";
import LockedFeatureCard from "@/components/pricing/LockedFeatureCard";
import { useCapabilities } from "@/hooks/useCapabilities";
import { compactCityLabel } from "@/lib/geography";
import { cn } from "@/lib/utils";
import type { AlternativeRoute } from "@/types/routing";

type RoutingStrategiesProps = {
  strategies: AlternativeRoute[];
  activeStrategy: string;
  onSelect: (kind: string) => void;
  onRefresh: () => void;
  refreshing: boolean;
  recommendedCount?: number;
  recommendedPlan?: {
    ordered_city_names: string[];
    drive_hours_matrix: Record<string, Record<string, number>>;
  } | null;
};

const RECOMMENDED_STRATEGY = "recommended";
const RECOMMENDED_NOTE =
  "Nearest-neighbor sequence that balances drive times against the states + pins you supplied.";

export function RoutingStrategies({
  strategies,
  activeStrategy,
  onSelect,
  onRefresh,
  refreshing,
  recommendedCount = 0,
  recommendedPlan,
}: RoutingStrategiesProps) {
  const { openUpgradeModal } = useCapabilities();
  const strategyCards = useMemo(() => {
    const unique = new Map<string, AlternativeRoute>();
    strategies.forEach((route) => {
      if (!unique.has(route.kind)) {
        unique.set(route.kind, route);
      }
    });
    const ordered = Array.from(unique.values());
    const recommendedMetrics = recommendedPlan
      ? computeMetrics(recommendedPlan.ordered_city_names, recommendedPlan.drive_hours_matrix)
      : null;
    return [
      {
        kind: RECOMMENDED_STRATEGY,
        title: "Recommended",
        description: RECOMMENDED_NOTE,
        cityCount: recommendedCount,
        metrics: recommendedMetrics,
      },
      ...ordered.map((route) => ({
        kind: route.kind,
        title: route.kind.charAt(0).toUpperCase() + route.kind.slice(1),
        description: route.notes || "Heuristic route suggestion.",
        cityCount: route.ordered_city_names?.length ?? 0,
        metrics: computeMetrics(route.ordered_city_names, route.drive_hours_matrix),
      })),
    ];
  }, [recommendedCount, recommendedPlan, strategies]);

  return (
    <CapabilityGate
      flag="canUseRoutingStrategies"
      fallback={
        <LockedFeatureCard
          title="Routing strategies"
          description="Upgrade to Pro to unlock alternative heuristics, cluster logic, and AI route variants."
          onUpgrade={() => openUpgradeModal("Routing strategies")}
        />
      }
    >
      <Card className="aa-glass-card rounded-2xl">
      <CardHeader className="flex flex-col gap-3 px-6 pt-6 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="text-lg font-semibold tracking-wide text-foreground/90">Routing strategies</CardTitle>
        <Button size="sm" variant="outline" onClick={onRefresh} disabled={refreshing}>
          {refreshing ? (
            <>
              <Loader2 className="animate-spin" />
              Refreshing…
            </>
          ) : (
            "Regenerate"
          )}
        </Button>
      </CardHeader>
      <CardContent className="px-6 pb-6">
        <div className="grid gap-4 lg:grid-cols-3">
          {strategyCards.map((strategy) => {
            const isActive = strategy.kind === activeStrategy || (!strategies.length && strategy.kind === RECOMMENDED_STRATEGY);
            return (
              <button
                key={strategy.kind}
                type="button"
                className={cn(
                  "rounded-2xl border px-4 py-3 text-left transition-colors",
                  isActive
                    ? "border-amber-300/80 bg-amber-400/15 text-primary-foreground shadow-lg"
                    : "border-border/30 bg-background/15 text-foreground/90 shadow-sm hover:border-amber-300/60 hover:bg-background/20",
                )}
                onClick={() => onSelect(strategy.kind)}
              >
                <p
                  className={`text-xs uppercase tracking-wide ${
                    isActive ? "text-rose-400" : "text-amber-300"
                  }`}
                >
                  {strategy.kind === RECOMMENDED_STRATEGY ? "Default" : "Heuristic"}
                </p>
                <p
                  className={`mt-1 text-lg font-semibold ${
                    isActive ? "text-primary-foreground" : "text-foreground"
                  }`}
                >
                  {strategy.title}
                </p>
                <p
                  className={`mt-2 text-sm ${
                    isActive ? "text-primary-foreground/80" : "text-foreground/70"
                  }`}
                >
                  {strategy.description}
                </p>
                {strategy.cityCount ? (
                  <p
                    className={`mt-2 text-xs ${
                      isActive ? "text-primary-foreground/80" : "text-muted-foreground"
                    }`}
                  >
                    {strategy.cityCount} stops
                  </p>
                ) : null}
                {strategy.metrics ? (
                  <div
                    className={`mt-3 grid gap-2 text-[11px] ${
                      isActive ? "text-primary-foreground/80" : "text-muted-foreground"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>Total hours</span>
                      <span>{strategy.metrics.totalHours.toFixed(1)} h</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Max gap</span>
                      <span>{strategy.metrics.maxGap.toFixed(1)} h</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Backtracks</span>
                      <span>{strategy.metrics.backtracks}</span>
                    </div>
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
        {!strategies.length ? (
          <p className="mt-4 text-sm text-muted-foreground">
            Generate a routing plan or refresh strategies to unlock balanced, aggressive, and premium heuristics.
          </p>
        ) : null}
      </CardContent>
      </Card>
    </CapabilityGate>
  );
}

function computeMetrics(
  orderedCityNames: string[],
  driveMatrix: Record<string, Record<string, number>>,
) {
  if (!orderedCityNames?.length) return null;
  const legs = orderedCityNames
    .map((label, index, arr) => {
      if (index === arr.length - 1) return null;
      const next = arr[index + 1];
      const hours = driveMatrix?.[label]?.[next];
      return typeof hours === "number" ? hours : null;
    })
    .filter((value): value is number => typeof value === "number");
  if (!legs.length) return null;

  const normalizedLabels = orderedCityNames.map((label) => {
    const [rawCity, rawState = ""] = label.split(",").map((value) => value.trim());
    return compactCityLabel(rawCity, rawState);
  });
  const firstLabel = normalizedLabels[0];
  const lastLabel = normalizedLabels[normalizedLabels.length - 1];
  const ignoreFinalReturn = firstLabel && lastLabel && firstLabel === lastLabel;
  const seen = new Set<string>();
  let backtracks = 0;
  normalizedLabels.forEach((label, index) => {
    if (!label) return;
    if (index === normalizedLabels.length - 1 && ignoreFinalReturn) {
      return;
    }
    if (seen.has(label)) {
      backtracks += 1;
    } else {
      seen.add(label);
    }
  });

  return {
    totalHours: legs.reduce((sum, value) => sum + value, 0),
    maxGap: Math.max(...legs),
    backtracks,
  };
}
